import { useEffect, useState } from 'react';
import { CCM_BANNER_ID, CCM_SCRIPT_ID } from '@/app/common/ccm/ccmElements';
import { getCookie } from '@/app/common/cookies/clientCookies';
import { COOKIE_KEY } from '@/app/common/cookies/types';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import { watchForConsent } from '@/app/common/ccm/consentWatch';

// How long the CCM gets to put its banner up before it is taken not to be coming. On production the
// banner lands about 2.4s after navigation starts (measured twice on the MPH-1137 url, warm connection),
// roughly a second after hydration, so this leaves it a wide margin. A banner that turns up later than
// this still works under a modal thanks to the MPH-1137 rules in globals.css, so erring short here costs
// less than erring long: a long wait is a deeplink the user is still staring at an unopened page for.
export const CCM_BANNER_GRACE_MS = 5000;

type Options = {
  // Also settle when no banner is going to come: the CCM script tag is not on the page at all (the app
  // webview, where PageWrapperServerExtras leaves it out), or the grace period has passed with no banner
  // in the DOM (script blocked by an ad blocker with a cookie-notice list, CDN down, CCM threw, no banner
  // required). Off by default on purpose: the search popups that opened on this hook before MPH-1137
  // never opened inside the app, and switching them on there is a product decision, not a bug fix.
  settleWithoutBanner?: boolean;
  // False when the caller has nothing to wait for; the hook then reports true and never starts polling.
  enabled?: boolean;
};

const hasConsentCookie = () => !!getCookie(COOKIE_KEY.COOKIE_CONSENT_MANAGER);
const isCCMScriptOnPage = () => !!document.getElementById(CCM_SCRIPT_ID);
const isBannerUp = () => !!document.getElementById(CCM_BANNER_ID);

// The banner is out of the way once the user has answered it — whatever they answered — or, for callers
// that asked for it, once it is clear that no banner is coming.
const isCCMSettled = (isLocal: boolean | undefined, settleWithoutBanner: boolean, startedAt: number) => {
  if (isLocal === true || hasConsentCookie()) return true;
  if (!settleWithoutBanner || !canUseDOM) return false;
  if (!isCCMScriptOnPage()) return true;
  return Date.now() - startedAt >= CCM_BANNER_GRACE_MS && !isBannerUp();
};

// Returns true once the Cookie Consent Manager banner is no longer in the way.
// Use this to avoid showing modals while the CCM banner is still visible.
const useCCMShown = (isLocal?: boolean, { settleWithoutBanner = false, enabled = true }: Options = {}): boolean => {
  const [startedAt] = useState(() => Date.now());
  const [isShown, setIsShown] = useState<boolean>(() => isCCMSettled(isLocal, settleWithoutBanner, startedAt));

  useEffect(() => {
    if (!enabled || isShown) return undefined;

    if (isCCMSettled(isLocal, settleWithoutBanner, startedAt)) {
      setIsShown(true);
      return undefined;
    }

    return watchForConsent(() => {
      if (isCCMSettled(isLocal, settleWithoutBanner, startedAt)) setIsShown(true);
    });
  }, [enabled, isShown, isLocal, settleWithoutBanner, startedAt]);

  return enabled ? isShown : true;
};

export default useCCMShown;
